import { Animation, JointType } from './Animation';
import { AnimationFrame } from './AnimationFrame';
import { mat4, quat, vec3 } from 'gl-matrix';
import { getNumComponents, Gltf, GltfAccessor, GltfAnimationChannel } from '../gltf';

export class GltfAnimation extends Animation {
  public clips: { id: string; channels: GltfAnimationChannel[] }[]; // mimic ColladaAnimation.clips
  public clipLength: { [key: string]: number };

  public bindShapeMatrix!: mat4;
  public joints!: JointType[];
  public skinJointIndices: number[] = [];
  public jointParentMap: number[] = [];

  public validAnimation: boolean;

  public get reference(): string {
    return 'gltf-animation'; // TODO: fix
  }

  private jointIndexMap = new Map<string, number>();

  // keyed by node index, not object
  private invBindByIndex = new Map<number, mat4>();

  // Sample a sampler's input/output into numeric arrays
  private sampleTimesAndValues(samplerIndex: number) {
    const anim = this.gltfObject.animations!.find(
      (a) => a.samplers && a.samplers.length > samplerIndex,
    )!;
    const sampler = anim.samplers[samplerIndex];
    const inputA = this.gltfObject.accessors![sampler.input];
    const outputA = this.gltfObject.accessors![sampler.output];
    const times = this.readAccessor(inputA, this.buffers); // Float32Array
    const values = this.readAccessor(outputA, this.buffers); // Float32Array
    return {
      times,
      values,
      interpolation: sampler.interpolation || 'LINEAR',
      outputType: outputA.type,
    };
  }

  // compose TRS into mat4
  private composeTRS(t: vec3, r: quat, s: vec3): mat4 {
    const m = mat4.create();
    mat4.fromRotationTranslationScale(m, r, t, s);
    return m;
  }

  constructor(
    private gltfObject: Gltf,
    private buffers: Uint8Array[],
  ) {
    super();
    this.clips = [];
    this.clipLength = {};
    this.validAnimation = false;
  }

  public preComputeAnimations() {
    this.buildJoints();

    const animations = this.gltfObject!.animations || [];
    for (let ai = 0; ai < animations.length; ai++) {
      this.validAnimation = true;

      const anim = animations[ai];
      const clipId = anim.name || `Anim${ai}`;
      this.clips.push({ id: clipId, channels: anim.channels });

      // Build a mapping: nodeIndex -> { times, translate[], rotate[], scale[] }
      // For simplicity we assume all samplers in this animation share the same times per-target node (common exporters do).
      const nodeChannelMap = new Map<
        number,
        {
          times?: Float32Array;
          translations?: Float32Array; // flat array length = times.length * 3
          rotations?: Float32Array; // length = times.length * 4
          scales?: Float32Array; // length = times.length * 3
        }
      >();

      // First, discover nodes targeted by channels and allocate arrays
      let globalFrameCount = 0;
      let globalTimes: Float32Array | undefined = undefined;
      for (const ch of anim.channels) {
        const nodeIndex = ch.target.node;
        const sampler = anim.samplers[ch.sampler];
        const inputAcc = this.gltfObject.accessors![sampler.input];
        const times = this.readAccessor(inputAcc, this.buffers);

        if (times.length > globalFrameCount) {
          globalFrameCount = times.length;
          globalTimes = times;
        }

        if (!nodeChannelMap.has(nodeIndex)) nodeChannelMap.set(nodeIndex, {});
        const entry = nodeChannelMap.get(nodeIndex)!;

        // choose the longest times array we encounter for this node (avoid undersizing)
        if (!entry.times || times.length > entry.times.length) {
          entry.times = times;
        }
      }

      // Initialize arrays per node at globalFrameCount size, filled with defaults
      for (const [nodeIndex, entry] of nodeChannelMap.entries()) {
        const node = this.gltfObject.nodes![nodeIndex];
        const defT = node.translation ? node.translation.slice() : [0, 0, 0];
        const defR = node.rotation ? node.rotation.slice() : [0, 0, 0, 1];
        const defS = node.scale ? node.scale.slice() : [1, 1, 1];

        entry.translations = new Float32Array(globalFrameCount * 3);
        entry.rotations = new Float32Array(globalFrameCount * 4);
        entry.scales = new Float32Array(globalFrameCount * 3);

        for (let f = 0; f < globalFrameCount; f++) {
          entry.translations.set(defT, f * 3);
          entry.rotations.set(defR, f * 4);
          entry.scales.set(defS, f * 3);
        }
        // Override entry.times to globalTimes so frame creation uses it
        entry.times = globalTimes;
      }

      // Fill arrays from channels, resampling to globalTimes when keyframe count differs
      for (const ch of anim.channels) {
        const sampler = anim.samplers[ch.sampler];
        const inputAcc = this.gltfObject.accessors![sampler.input];
        const outputAcc = this.gltfObject.accessors![sampler.output];
        const srcTimes = this.readAccessor(inputAcc, this.buffers);
        const values = this.readAccessor(outputAcc, this.buffers);
        const nodeIndex = ch.target.node;
        const entry = nodeChannelMap.get(nodeIndex)!;
        if (!entry) continue;

        const isRotation = ch.target.path === 'rotation';
        const comp = isRotation ? 4 : 3;
        const srcFrameCount = Math.floor(values.length / comp);
        const dst =
          ch.target.path === 'translation'
            ? entry.translations!
            : isRotation
              ? entry.rotations!
              : ch.target.path === 'scale'
                ? entry.scales!
                : null;
        if (!dst) continue;

        if (srcFrameCount === globalFrameCount) {
          // Direct copy — same keyframe count
          for (let f = 0; f < srcFrameCount; f++) {
            dst.set(values.subarray(f * comp, f * comp + comp), f * comp);
          }
        } else {
          // Resample: interpolate source keyframes at globalTimes
          for (let f = 0; f < globalFrameCount; f++) {
            const t = globalTimes![f];
            // Find surrounding source keyframes
            let lo = 0;
            for (let k = 0; k < srcFrameCount - 1; k++) {
              if (srcTimes[k + 1] <= t) lo = k + 1;
              else break;
            }
            const hi = Math.min(lo + 1, srcFrameCount - 1);
            if (lo === hi || srcTimes[hi] === srcTimes[lo]) {
              // Exact match or clamped — copy directly
              dst.set(values.subarray(lo * comp, lo * comp + comp), f * comp);
            } else {
              const alpha = (t - srcTimes[lo]) / (srcTimes[hi] - srcTimes[lo]);
              if (isRotation) {
                // Slerp for quaternions
                const q0 = quat.fromValues(
                  values[lo * 4],
                  values[lo * 4 + 1],
                  values[lo * 4 + 2],
                  values[lo * 4 + 3],
                );
                const q1 = quat.fromValues(
                  values[hi * 4],
                  values[hi * 4 + 1],
                  values[hi * 4 + 2],
                  values[hi * 4 + 3],
                );
                const out = quat.create();
                quat.slerp(out, q0, q1, alpha);
                dst.set([out[0], out[1], out[2], out[3]], f * 4);
              } else {
                // Lerp for translation/scale
                for (let c = 0; c < comp; c++) {
                  dst[f * comp + c] =
                    values[lo * comp + c] + alpha * (values[hi * comp + c] - values[lo * comp + c]);
                }
              }
            }
          }
        }
      }

      // Now compose per-node per-frame matrices and write to node.frames[clipId]
      this.clipLength[clipId] = globalFrameCount;
      for (const [nodeIndex, entry] of nodeChannelMap.entries()) {
        const node = this.gltfObject.nodes![nodeIndex];
        node.animated = true;
        if (!node.frames) node.frames = {};
        const frames: AnimationFrame[] = new Array(globalFrameCount);
        let last: AnimationFrame | undefined = undefined;

        for (let f = 0; f < globalFrameCount; f++) {
          const t = vec3.fromValues(
            entry.translations![f * 3 + 0],
            entry.translations![f * 3 + 1],
            entry.translations![f * 3 + 2],
          );
          const r = quat.fromValues(
            entry.rotations![f * 4 + 0],
            entry.rotations![f * 4 + 1],
            entry.rotations![f * 4 + 2],
            entry.rotations![f * 4 + 3],
          );
          const s = vec3.fromValues(
            entry.scales![f * 3 + 0],
            entry.scales![f * 3 + 1],
            entry.scales![f * 3 + 2],
          );
          const m = this.composeTRS(t, r, s);
          const af = new AnimationFrame(globalTimes![f], m, 'LINEAR');
          af.previousFrame = last;
          if (last) last.nextFrame = af;
          last = af;
          frames[f] = af;
        }

        node.frames[clipId] = frames;
      }

      // Finally compute joint matrices for every frame (match Collada ordering)
      // Choose root like Collada: the controller root -> here we pick skin.skeleton or first scene root
      const skin = this.gltfObject.skins![0];
      const rootIndex =
        skin.skeleton ?? this.gltfObject.scenes?.[this.gltfObject.scene || 0].nodes?.[0] ?? 0;

      for (let f = 0; f < globalFrameCount; f++) {
        this.computeMatricesForFrame(rootIndex, f, clipId);
      }
    } // end animations loop
  }

  private buildJoints() {
    const skin = this.gltfObject!.skins?.[0]; // assuming single skin
    if (!skin) throw new Error('No skin in glTF');

    // bindShapeMatrix is optional
    this.bindShapeMatrix = skin.bindShapeMatrix ? mat4.clone(skin.bindShapeMatrix) : mat4.create();

    const invBindAccessor = this.gltfObject!.accessors![skin.inverseBindMatrices!];
    const invBindData = this.readAccessor(invBindAccessor, this.buffers); // Float32Array length = joints * 16

    this.invBindByIndex = new Map();
    this.joints = [];
    this.skinJointIndices = skin.joints.slice();

    const jointSet = new Set(skin.joints);
    const nodeToSkinIndex = new Map<number, number>();

    for (let i = 0; i < skin.joints.length; i++) {
      const nodeIndex = skin.joints[i];
      const invBindMat = this.toMat4(invBindData, i); // toMat4 expects index i (offset = i*16)
      this.invBindByIndex.set(nodeIndex, invBindMat);
      const node = this.gltfObject?.nodes?.[nodeIndex]!;
      this.joints.push(node);
      this.jointIndexMap.set(node.name, i);
      nodeToSkinIndex.set(nodeIndex, i);
    }

    // Build parent map: jointParentMap[childSkinIndex] = parentSkinIndex (-1 if root)
    this.jointParentMap = new Array(skin.joints.length).fill(-1);
    for (const nodeIdx of skin.joints) {
      const node = this.gltfObject!.nodes![nodeIdx];
      for (const childIdx of node.children ?? []) {
        if (jointSet.has(childIdx)) {
          this.jointParentMap[nodeToSkinIndex.get(childIdx)!] = nodeToSkinIndex.get(nodeIdx)!;
        }
      }
    }
  }

  public getJointIndex(name: string): number | undefined {
    return this.jointIndexMap.get(name);
  }

  /** Compute world-space bind-pose positions for all joints in one pass. */
  public computeBindPosePositions(): vec3[] {
    const nodes = this.gltfObject.nodes!;
    const skin = this.gltfObject.skins![0];
    const rootIdx = skin.skeleton ?? this.gltfObject.scenes?.[0]?.nodes?.[0] ?? 0;

    // Build parent-of map for all nodes (not just joints)
    const parentOf = new Map<number, number>();
    const buildParents = (idx: number) => {
      const node = nodes[idx];
      for (const child of node.children ?? []) {
        parentOf.set(child, idx);
        buildParents(child);
      }
    };
    buildParents(rootIdx);

    // Cache world matrices as we go to avoid recomputation
    const worldMatrices = new Map<number, mat4>();
    const getWorldMatrix = (nodeIdx: number): mat4 => {
      if (worldMatrices.has(nodeIdx)) return worldMatrices.get(nodeIdx)!;

      const node = nodes[nodeIdx];
      const local = mat4.create();
      const t = node.translation ?? [0, 0, 0];
      const r = node.rotation ?? [0, 0, 0, 1];
      const s = node.scale ?? [1, 1, 1];
      mat4.fromRotationTranslationScale(
        local,
        [r[0], r[1], r[2], r[3]],
        [t[0], t[1], t[2]],
        [s[0], s[1], s[2]],
      );

      const parent = parentOf.get(nodeIdx);
      if (parent !== undefined) {
        const world = mat4.create();
        mat4.multiply(world, getWorldMatrix(parent), local);
        worldMatrices.set(nodeIdx, world);
        return world;
      }

      worldMatrices.set(nodeIdx, local);
      return local;
    };

    return this.skinJointIndices.map((nodeIdx) => {
      const world = getWorldMatrix(nodeIdx);
      return vec3.fromValues(world[12], world[13], world[14]);
    });
  }

  private toMat4(data: Float32Array, offset: number): mat4 {
    const m = mat4.create();
    for (let j = 0; j < 16; j++) {
      m[j] = data[offset * 16 + j];
    }
    return m;
  }

  private localMatrixForNode(nodeIndex: number, frameIndex: number, clipId: string): mat4 {
    const node = this.gltfObject.nodes![nodeIndex];
    // If animated and frames exist for this clip, use that matrix
    if (node.animated && node.frames && node.frames[clipId] && node.frames[clipId][frameIndex]) {
      // In your code you stored matrix in AnimationFrame as a mat4 already
      return node.frames[clipId][frameIndex].matrix;
    }
    // Otherwise, prefer explicit node.matrix if present (it's a local matrix)
    if (node.matrix) {
      // node.matrix is usually a flat number[] of length 16
      return mat4.clone(node.matrix as mat4);
    }

    // Otherwise compose from TRS or defaults
    const T = node.translation ? vec3.fromValues(...node.translation) : vec3.fromValues(0, 0, 0);
    const R = node.rotation ? quat.fromValues(...node.rotation) : quat.fromValues(0, 0, 0, 1);
    const S = node.scale ? vec3.fromValues(...node.scale) : vec3.fromValues(1, 1, 1);

    const m = mat4.create();
    mat4.fromRotationTranslationScale(m, R, T, S);
    return m;
  }

  private computeMatricesForFrame(rootIndex: number, frameIndex: number, clipId: string) {
    const nodes = this.gltfObject.nodes ?? [];
    const skin = this.gltfObject.skins![0];
    if (!skin) return;

    // jointMap keyed by node index (like Collada)
    const jointMap = new Map<number, mat4>();

    // For root, use the node's frame matrix (if animated) or its local TRS/matrix
    const rootMatrix = this.localMatrixForNode(rootIndex, frameIndex, clipId);
    jointMap.set(rootIndex, rootMatrix);

    if (skin.joints.indexOf(rootIndex) !== -1) {
      const invBind = this.invBindByIndex.get(rootIndex);
      if (invBind) {
        const finalMat = mat4.create();
        mat4.multiply(finalMat, rootMatrix, invBind); // jointWorld * invBind (glTF spec)
        mat4.transpose(finalMat, finalMat); // transpose for v * M shader convention
        const node = nodes[rootIndex];
        if (!node.frames) node.frames = {};
        if (!node.frames[clipId]) node.frames[clipId] = new Array(this.clipLength[clipId] || 0);
        const af = node.frames[clipId][frameIndex];
        if (af) {
          af.jointMatrix = rootMatrix;
          af.worldMatrix = finalMat;
          af.invBindMatrix = invBind;
          af.index = frameIndex;
        } else {
          const fb = new AnimationFrame(0, mat4.create(), 'LINEAR');
          fb.jointMatrix = rootMatrix;
          fb.worldMatrix = finalMat;
          fb.invBindMatrix = invBind;
          fb.index = frameIndex;
          node.frames[clipId][frameIndex] = fb;
        }
      }
    }

    const traverse = (nodeIndex: number) => {
      const node = nodes[nodeIndex];
      if (!node) return;

      for (const childIndex of node.children ?? []) {
        // Standard glTF: worldMatrix = parentWorld * localMatrix
        const animMatrix = this.localMatrixForNode(childIndex, frameIndex, clipId);
        const parentMatrix = jointMap.get(nodeIndex);
        const jointMatrix = mat4.create();
        mat4.multiply(jointMatrix, parentMatrix!, animMatrix); // parent * local (standard glTF)
        jointMap.set(childIndex, jointMatrix);

        // if child is a joint, compute final and store onto node.frames
        if (skin.joints.indexOf(childIndex) !== -1) {
          const invBind = this.invBindByIndex.get(childIndex);
          if (!invBind) {
            console.warn('Missing invBind for', childIndex);
          }
          const finalMat = mat4.create();
          mat4.multiply(finalMat, jointMatrix, invBind!); // jointWorld * invBind (glTF spec)
          mat4.transpose(finalMat, finalMat); // transpose for v * M shader convention
          // ensure node.frames[clipId][frameIndex] exists
          if (!nodes[childIndex].frames) nodes[childIndex].frames = {};
          if (!nodes[childIndex].frames[clipId])
            nodes[childIndex].frames[clipId] = new Array<AnimationFrame>(
              this.clipLength[clipId] || 0,
            );
          const af = nodes[childIndex].frames[clipId][frameIndex];
          if (af) {
            af.jointMatrix = jointMatrix;
            af.worldMatrix = finalMat;
            af.invBindMatrix = invBind!;
            af.index = frameIndex;
          } else {
            const fb = new AnimationFrame(0, mat4.create(), 'LINEAR');
            fb.jointMatrix = jointMatrix;
            fb.worldMatrix = finalMat;
            fb.invBindMatrix = invBind!;
            fb.index = frameIndex;
            nodes[childIndex].frames[clipId][frameIndex] = fb;
          }
        }

        // traverse deeper
        traverse(childIndex);
      }
    };

    // start recursion at root
    traverse(rootIndex);
  }

  private readAccessor(accessor: GltfAccessor, buffers: Uint8Array[]): Float32Array {
    const bufferView = this.gltfObject.bufferViews![accessor.bufferView!];
    const buffer = buffers[bufferView.buffer];
    const byteOffset = (bufferView.byteOffset || 0) + (accessor.byteOffset || 0);
    const length = accessor.count * getNumComponents(accessor.type);
    return new Float32Array(buffer.buffer, buffer.byteOffset + byteOffset, length);
  }
}
